import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDSnackbar from "components/MDSnackbar";
import apiClient from "utils/apiClient";
import { getAuthToken, logout } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import profileImage from "assets/images/avatar.png";
import { RED, RED_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";
import { useUnitSystem } from "context/UnitSystemContext";
import { buildAvoidOptions, selectionOption, suggestAvoidOptions } from "utils/avoidOptions";
import DietSelector from "components/DietSelector";
import { excludedGroupsFor, useDietPreference } from "utils/dietPreference";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "14px",
  border: "1.5px solid",
  borderColor: BORDER,
  overflow: "hidden",
  mb: 2,
};

// Behind the "?" on the taste card. Two facts worth being able to look up and
// not worth reading before the card itself: the model is strictly personal, and
// it biases rather than filters.
const TASTE_PROVENANCE_COPY =
  "Built only from your own cooking, swaps and ratings — never from other people's. " +
  "Nudges your schedules; never rules anything out.";

// Dimension keys that are residual buckets rather than opinions. "Other"
// cuisine and the middle time band are internal vocabulary: a user cannot act
// on "you prefer Other", and neither row is a statement they would recognise
// as their own. The belief still exists and still biases — this hides a row
// that could not be read, not one that could not be corrected.
const UNNAMEABLE_BELIEFS = new Set(["cuisine:other", "time_bucket:medium"]);

const isNameableBelief = (belief) => !UNNAMEABLE_BELIEFS.has(`${belief.dimension}:${belief.key}`);

const cardLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: MUTED,
  fontFamily: "Roboto, sans-serif",
};

// ── Inline-editable account row ──
function InfoRow({ label, value, type = "text", onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const save = async () => {
    if (draft !== value) await onSave(draft);
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 3,
        py: "13px",
        borderBottom: "1px solid #f5f5f5",
        gap: 1.5,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          fontSize: 12,
          color: MUTED,
          width: 80,
          flexShrink: 0,
          fontFamily: "Roboto, sans-serif",
        }}
      >
        {label}
      </Box>

      {editing ? (
        <Box
          component="input"
          ref={inputRef}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") setEditing(false);
          }}
          sx={{
            flex: 1,
            border: "1.5px solid",
            borderColor: RED,
            borderRadius: "7px",
            padding: "5px 10px",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            outline: "none",
            color: "#222",
          }}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 500,
            color: type === "password" ? MUTED : "#222",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {type === "password" ? "••••••••" : value || "—"}
        </Box>
      )}

      {editing ? (
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Box
            component="button"
            onClick={save}
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#fff",
              bgcolor: RED,
              border: "none",
              borderRadius: "6px",
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { bgcolor: "#7d1520" },
            }}
          >
            Save
          </Box>
          <Box
            component="button"
            onClick={() => {
              setEditing(false);
              setDraft(value);
            }}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#666",
              bgcolor: "#f0f0f0",
              border: "none",
              borderRadius: "6px",
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Cancel
          </Box>
        </Box>
      ) : (
        <Box
          component="button"
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: RED,
            bgcolor: RED_LIGHT,
            border: "1.5px solid",
            borderColor: RED_LIGHT,
            borderRadius: "6px",
            padding: "3px 10px",
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            flexShrink: 0,
            "&:hover": { bgcolor: "#f5c0c5" },
          }}
        >
          {label === "Password" ? "Change" : "Edit"}
        </Box>
      )}
    </Box>
  );
}

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  type: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

// ── Pantry chip search input ──
function PantrySearch({ masterList, userItems, onAdd }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const filtered = masterList
    .filter((i) => !userItems.find((u) => u.id === i.id))
    .filter((i) => i.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 6);

  const pick = (item) => {
    onAdd(item);
    setQuery("");
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        px: 3,
        py: 2,
        borderBottom: "1px solid",
        borderColor: BORDER,
        position: "relative",
      }}
    >
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          component="input"
          ref={inputRef}
          value={query}
          placeholder="Search to add an item…"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          sx={{
            width: "100%",
            padding: "9px 14px",
            border: "1.5px solid",
            borderColor: BORDER,
            borderRadius: "8px",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            outline: "none",
            color: "#222",
            "&:focus": { borderColor: RED },
            "&::placeholder": { color: MUTED },
            transition: "border-color 0.15s",
          }}
        />
        {open && filtered.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              bgcolor: "#fff",
              border: "1.5px solid",
              borderColor: BORDER,
              borderRadius: "8px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.07)",
              overflow: "hidden",
              zIndex: 50,
            }}
          >
            {filtered.map((item) => (
              <Box
                key={item.id}
                onMouseDown={() => pick(item)}
                sx={{
                  padding: "10px 14px",
                  fontSize: 13,
                  fontFamily: "Roboto, sans-serif",
                  cursor: "pointer",
                  "&:hover": { bgcolor: RED_LIGHT, color: RED },
                }}
              >
                {item.icon && (
                  <Box component="span" sx={{ mr: 1 }}>
                    {item.icon}
                  </Box>
                )}
                {item.name}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

PantrySearch.propTypes = {
  masterList: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, icon: PropTypes.string })
  ).isRequired,
  userItems: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }))
    .isRequired,
  onAdd: PropTypes.func.isRequired,
};

// ── Allergy search input (same pattern as PantrySearch) ──
// Offers ingredient families ("fish") alongside individual ingredients, from
// one list — see utils/avoidOptions.js. A family is one pickable entry and
// becomes one chip; the members it covers are never shown or stored.
function AllergySearch({ options, blacklisted, onAdd }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const filtered = suggestAvoidOptions(options, blacklisted, query);

  const pick = (item) => {
    onAdd(item);
    setQuery("");
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        px: 3,
        py: 2,
        borderBottom: "1px solid",
        borderColor: BORDER,
        position: "relative",
      }}
    >
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          component="input"
          ref={inputRef}
          value={query}
          placeholder="Search foods to avoid — try “fish” or “mushroom”…"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          sx={{
            width: "100%",
            padding: "9px 14px",
            border: "1.5px solid",
            borderColor: BORDER,
            borderRadius: "8px",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            outline: "none",
            color: "#222",
            "&:focus": { borderColor: RED },
            "&::placeholder": { color: MUTED },
            transition: "border-color 0.15s",
          }}
        />
        {open && filtered.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              bgcolor: "#fff",
              border: "1.5px solid",
              borderColor: BORDER,
              borderRadius: "8px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.07)",
              overflow: "hidden",
              zIndex: 50,
            }}
          >
            {filtered.map((item) => (
              <Box
                key={item.key}
                onMouseDown={() => pick(item)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  padding: "10px 14px",
                  fontSize: 13,
                  fontFamily: "Roboto, sans-serif",
                  cursor: "pointer",
                  "&:hover": { bgcolor: RED_LIGHT, color: RED },
                }}
              >
                <Box component="span">{item.name}</Box>
                {item.kind === "group" && (
                  // Says what one tap actually does. "fish" and "salmon" look
                  // identical in a list until one of them tells you it covers
                  // six things.
                  <Box component="span" sx={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>
                    all {item.memberCount} · group
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

AllergySearch.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      kind: PropTypes.string,
      name: PropTypes.string,
      memberCount: PropTypes.number,
    })
  ).isRequired,
  blacklisted: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  onAdd: PropTypes.func.isRequired,
};

const PANTRY_ICON = (name) => {
  const n = (name || "").toLowerCase();
  if (
    /herb|basil|parsley|dill|mint|thyme|rosemary|oregano|sage|chive|cilantro|spice|pepper|paprika|cumin|coriander|turmeric|cinnamon|nutmeg|clove|cardamom|chili|cayenne|bay leaf|garlic powder|onion powder/.test(
      n
    )
  )
    return "🌿";
  if (
    /oil|vinegar|sauce|soy|tahini|mustard|ketchup|mayo|honey|syrup|stock|broth|wine|worcestershire/.test(
      n
    )
  )
    return "🫙";
  if (/flour|starch|cornmeal|breadcrumb|oat|grain|rice|pasta|noodle|cereal|crumb/.test(n))
    return "🌾";
  if (/can|canned|tomato paste|tomato sauce|bean|chickpea|lentil|sardine|tuna/.test(n)) return "🥫";
  return "🧂";
};

/**
 * What the app has worked out about this user's taste, and the controls to
 * correct it. A model the user cannot see or fix is worse than no model.
 *
 * Two rules the layout has to preserve:
 *   - Beliefs and guesses stay visually separate. `still_learning` items are
 *     things the app has noticed but is NOT acting on; presenting them
 *     alongside real beliefs would invite corrections to something that was
 *     never affecting anything.
 *   - When the model is inactive, say so plainly rather than showing an empty
 *     "your tastes" panel that implies learning is happening.
 */
function TasteProfile({ profile, onOverride, onClear }) {
  if (!profile) return null;

  const stillLearning = (profile.still_learning || []).filter(isNameableBelief);

  const rowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    px: 3,
    py: 1.5,
    borderTop: "1px solid",
    borderColor: BORDER,
  };

  const buttonSx = (activeState) => ({
    padding: "4px 10px",
    bgcolor: activeState ? "#222" : "#f5f5f5",
    color: activeState ? "#fff" : "#666",
    border: "none",
    borderRadius: "6px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Roboto, sans-serif",
  });

  const renderRow = (belief, { muted }) => (
    <Box key={`${belief.dimension}:${belief.key}`} sx={rowSx}>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: muted ? MUTED : "#222",
            fontFamily: "Roboto, sans-serif",
            textTransform: "capitalize",
          }}
        >
          {belief.label}
        </Box>
        <Box sx={{ fontSize: 11, color: MUTED, fontFamily: "Roboto, sans-serif", mt: "2px" }}>
          {/* No strength qualifier. "moderate signal" / "slight signal" asked
              the user to weigh a confidence band before deciding whether to
              press More or Less, and the buttons do the same thing either
              way. */}
          {belief.overridden
            ? "You set this"
            : `${belief.direction > 0 ? "More" : "Fewer"} of these`}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
        <Box
          component="button"
          sx={buttonSx(belief.overridden && belief.direction > 0)}
          onClick={() => onOverride(belief.dimension, belief.key, 1)}
        >
          More
        </Box>
        <Box
          component="button"
          sx={buttonSx(belief.overridden && belief.direction < 0)}
          onClick={() => onOverride(belief.dimension, belief.key, -1)}
        >
          Less
        </Box>
        {belief.overridden && (
          <Box
            component="button"
            sx={buttonSx(false)}
            onClick={() => onClear(belief.dimension, belief.key)}
          >
            Reset
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ ...cardSx, overflow: "visible" }}>
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
        {/* The provenance line ("built only from your own cooking… never rules
            anything out") moved into the heading's tooltip. It is true and
            worth being able to find, but it answered a question nobody had
            asked yet, above the rows that were the point of the card. */}
        <Tooltip title={TASTE_PROVENANCE_COPY} enterTouchDelay={0} leaveTouchDelay={6000}>
          <Box
            sx={{
              ...cardLabelSx,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: "help",
            }}
          >
            What We&apos;ve Learned
            <Box
              component="span"
              aria-label="How this is built"
              sx={{
                width: 15,
                height: 15,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: MUTED,
                color: MUTED,
                fontSize: 10,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              ?
            </Box>
          </Box>
        </Tooltip>
      </Box>

      {!profile.active && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 3,
            fontSize: 13,
            color: MUTED,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Still getting to know you — cook and plan a few more meals and your schedules will start
          adapting.
          {profile.events_needed > 0 && (
            <Box sx={{ fontSize: 11, mt: "6px" }}>
              About {profile.events_needed} more{" "}
              {profile.events_needed === 1 ? "action" : "actions"} to go.
            </Box>
          )}
        </Box>
      )}

      {profile.beliefs
        .filter(isNameableBelief)
        .map((belief) => renderRow(belief, { muted: false }))}

      {stillLearning.length > 0 && (
        <>
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 0.5,
              fontSize: 11,
              fontWeight: 700,
              color: MUTED,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontFamily: "Roboto, sans-serif",
              borderTop: "1px solid",
              borderColor: BORDER,
            }}
          >
            Not sure yet
          </Box>
          <Box sx={{ px: 3, pb: 1, fontSize: 11, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
            We&apos;ve noticed these but aren&apos;t acting on them yet. Set one directly if
            we&apos;ve got it right.
          </Box>
          {stillLearning.slice(0, 6).map((belief) => renderRow(belief, { muted: true }))}
        </>
      )}
    </Box>
  );
}

TasteProfile.propTypes = {
  profile: PropTypes.shape({
    active: PropTypes.bool,
    events_needed: PropTypes.number,
    beliefs: PropTypes.array,
    still_learning: PropTypes.array,
  }),
  onOverride: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

function Profile() {
  const navigate = useNavigate();
  const { metric, setMetric } = useUnitSystem();
  const [user, setUser] = useState({ username: "", email: "" });
  const [avatar, setAvatar] = useState(null);
  const thumbnailRef = useRef(null);
  const [pantryItems, setPantryItems] = useState([]);
  const [masterList, setMasterList] = useState([]);
  const [blacklisted, setBlacklisted] = useState([]);
  const [ingredientList, setIngredientList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const { diet, options: dietOptions, saveDiet } = useDietPreference();
  // Families first, then individual ingredients — one list for the picker,
  // minus the families the diet setting above already covers.
  const avoidOptions = useMemo(
    () => buildAvoidOptions(groupList, ingredientList, excludedGroupsFor(diet, dietOptions)),
    [groupList, ingredientList, diet, dietOptions]
  );
  const [tasteProfile, setTasteProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, content: "" });

  useEffect(() => {
    if (!getAuthToken()) {
      navigate("/authentication/sign-in");
      return;
    }
    Promise.all([
      apiClient.get(ENDPOINTS.users.me),
      apiClient.get(ENDPOINTS.pantry.myItems),
      apiClient.get(ENDPOINTS.pantry.pantryNameList),
      apiClient.get(ENDPOINTS.pantry.blacklisted),
      apiClient.get(ENDPOINTS.pantry.ingredientNameList),
      apiClient.get(ENDPOINTS.taste.profile),
      apiClient.get(ENDPOINTS.pantry.ingredientGroups),
    ])
      .then(([userRes, pantryRes, masterRes, blacklistRes, ingredientRes, tasteRes, groupRes]) => {
        setUser({ username: userRes.data.username || "", email: userRes.data.email || "" });
        if (Array.isArray(pantryRes.data)) setPantryItems(pantryRes.data);
        if (Array.isArray(masterRes.data)) setMasterList(masterRes.data);
        if (Array.isArray(blacklistRes.data))
          setBlacklisted(blacklistRes.data.map(selectionOption));
        if (Array.isArray(ingredientRes.data)) setIngredientList(ingredientRes.data);
        if (tasteRes.data) setTasteProfile(tasteRes.data);
        if (Array.isArray(groupRes.data)) setGroupList(groupRes.data);
      })
      .catch((e) => console.error(e.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  const updateUser = async (field, value) => {
    try {
      const res = await apiClient.put(ENDPOINTS.users.me, { [field]: value });
      setUser((prev) => ({ ...prev, [field]: res.data[field] ?? value }));
    } catch (err) {
      console.error("Failed to update user:", err.message);
    }
  };

  // Corrections are re-read from the server rather than patched locally: an
  // override changes which confidence gate a key sits behind, so a key can move
  // between "beliefs" and "not sure yet" as a result. Guessing that transition
  // client-side is how the two lists drift out of sync with what the scheduler
  // is actually using.
  const refreshTasteProfile = async () => {
    try {
      const res = await apiClient.get(ENDPOINTS.taste.profile);
      setTasteProfile(res.data);
    } catch (err) {
      console.error("Failed to load taste profile:", err.message);
    }
  };

  const setTasteOverride = async (dimension, key, direction) => {
    try {
      await apiClient.put(ENDPOINTS.taste.override(dimension, key), { direction });
      await refreshTasteProfile();
    } catch (err) {
      console.error("Failed to update preference:", err.message);
      setSnack({ open: true, content: "Couldn't save that preference." });
    }
  };

  const clearTasteOverride = async (dimension, key) => {
    try {
      await apiClient.delete(ENDPOINTS.taste.override(dimension, key));
      await refreshTasteProfile();
    } catch (err) {
      console.error("Failed to clear preference:", err.message);
      setSnack({ open: true, content: "Couldn't reset that preference." });
    }
  };

  // One list, two kinds of row. The body names exactly one target — the schema
  // and the DB both reject a row that names two — so which key is sent is the
  // only difference between avoiding a family and avoiding one ingredient.
  const addBlacklisted = async (item) => {
    try {
      await apiClient.post(
        ENDPOINTS.pantry.blacklisted,
        item.kind === "group" ? { group_slug: item.slug } : { ingredient_id: item.id }
      );
      setBlacklisted((prev) => [...prev, item]);
    } catch (err) {
      console.error("Failed to add to blacklist:", err.message);
    }
  };

  const removeBlacklisted = async (item) => {
    try {
      await apiClient.delete(
        item.kind === "group"
          ? ENDPOINTS.pantry.removeBlacklistedGroup(item.slug)
          : ENDPOINTS.pantry.removeBlacklisted(item.id)
      );
      setBlacklisted((prev) => prev.filter((i) => i.key !== item.key));
    } catch (err) {
      console.error("Failed to remove from blacklist:", err.message);
    }
  };

  const addPantryItem = async (item) => {
    try {
      await apiClient.post(ENDPOINTS.pantry.myItems, { pantry_item_id: item.id });
      setPantryItems((prev) => [...prev, item]);
    } catch (err) {
      console.error("Failed to add pantry item:", err.message);
    }
  };

  const removePantryItem = async (itemId) => {
    try {
      await apiClient.delete(ENDPOINTS.pantry.removeMyItem(itemId));
      setPantryItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (err) {
      console.error("Failed to remove pantry item:", err.message);
      setSnack({ open: true, content: err.response?.data?.detail || "Failed to remove item." });
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/authentication/sign-in");
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      await apiClient.delete(ENDPOINTS.users.deleteMe);
      logout();
      navigate("/authentication/sign-in");
    } catch (err) {
      console.error("Failed to delete account:", err.message);
      setSnack({
        open: true,
        content: err.response?.data?.detail || "Failed to delete account. Please try again.",
      });
    }
  };

  if (loading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          sx={{
            textAlign: "center",
            pt: 8,
            fontSize: 14,
            color: MUTED,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Loading…
        </Box>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6, px: { xs: 2, md: 3 }, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 560 }}>
          <Box
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: "#222",
              mb: 3,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Profile
          </Box>

          {/* Account card */}
          <Box sx={cardSx}>
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
              <Box sx={cardLabelSx}>Account</Box>
            </Box>

            {/* Avatar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2.5,
                borderBottom: "1px solid",
                borderColor: BORDER,
              }}
            >
              <Box
                onClick={() => thumbnailRef.current?.click()}
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  cursor: "pointer",
                  width: 56,
                  height: 56,
                }}
              >
                <Box
                  component="img"
                  src={avatar || profileImage}
                  alt="avatar"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid",
                    borderColor: BORDER,
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.15s",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 10,
                      color: "#fff",
                      fontWeight: 700,
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    Edit
                  </Box>
                </Box>
                <input
                  ref={thumbnailRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setAvatar(URL.createObjectURL(file));
                  }}
                />
              </Box>
              <Box>
                <Box
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#222",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {user.username || "—"}
                </Box>
                <Box
                  sx={{ fontSize: 12, color: MUTED, mt: "2px", fontFamily: "Roboto, sans-serif" }}
                >
                  {user.email}
                </Box>
              </Box>
            </Box>

            <InfoRow
              label="Username"
              value={user.username}
              onSave={(v) => updateUser("username", v)}
            />
            <InfoRow label="Email" value={user.email} onSave={(v) => updateUser("email", v)} />
            <InfoRow
              label="Password"
              value=""
              type="password"
              onSave={(v) => updateUser("password", v)}
            />
            {/* Unit system preference */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 1.75,
                borderTop: "1px solid",
                borderColor: BORDER,
              }}
            >
              <Box sx={{ fontSize: 13, color: MUTED, fontFamily: "Roboto, sans-serif" }}>Units</Box>
              <Box sx={{ display: "flex", gap: "6px" }}>
                {["US", "Metric"].map((opt) => {
                  const active = opt === "Metric" ? metric : !metric;
                  return (
                    <Box
                      key={opt}
                      component="button"
                      onClick={() => setMetric(opt === "Metric")}
                      sx={{
                        px: "12px",
                        py: "5px",
                        borderRadius: "7px",
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "Roboto, sans-serif",
                        cursor: "pointer",
                        border: "1.5px solid",
                        transition: "all 0.15s",
                        ...(active
                          ? { bgcolor: RED, color: "#fff", borderColor: RED }
                          : {
                              bgcolor: "#fff",
                              color: MUTED,
                              borderColor: BORDER,
                              "&:hover": { borderColor: RED, color: RED },
                            }),
                      }}
                    >
                      {opt}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* The skill grid used to sit here, below the password field, with no
              nav entry pointing at it. It now lives on Your Kitchen — one copy,
              in the nav, where it can be found on purpose. */}

          {/* Pantry card */}
          <Box sx={{ ...cardSx, overflow: "visible" }}>
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
              <Box sx={cardLabelSx}>My Cupboard</Box>
              <Box sx={{ fontSize: 12, color: MUTED, mt: "4px", fontFamily: "Roboto, sans-serif" }}>
                Items here won&apos;t appear in your grocery list
              </Box>
            </Box>

            <PantrySearch masterList={masterList} userItems={pantryItems} onAdd={addPantryItem} />

            {pantryItems.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  fontSize: 13,
                  color: MUTED,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                No items yet — search above to add some
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  px: 2,
                  py: 1,
                  pb: 2,
                }}
              >
                {pantryItems.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: "7px",
                      borderRadius: "6px",
                      "&:hover": { bgcolor: "#f9f9f9" },
                    }}
                  >
                    <Box component="span" sx={{ fontSize: 15, flexShrink: 0, lineHeight: 1 }}>
                      {PANTRY_ICON(item.name)}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 13,
                        color: "#222",
                        flex: 1,
                        fontFamily: "Roboto, sans-serif",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.name}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => removePantryItem(item.id)}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        color: MUTED,
                        bgcolor: "#f0f0f0",
                        border: "none",
                        cursor: "pointer",
                        flexShrink: 0,
                        lineHeight: 1,
                        transition: "all 0.15s",
                        "&:hover": { color: "#fff", bgcolor: "#e57373" },
                      }}
                    >
                      ✕
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* How you eat. Sits directly above the avoid list because the two are
              the same kind of rule at two different grains: this one statement
              replaces enumerating every animal ingredient by hand, and the list
              below is then for what a diet has no opinion about. */}
          <Box sx={cardSx}>
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
              <Box sx={cardLabelSx}>How You Eat</Box>
              <Box sx={{ fontSize: 12, color: MUTED, mt: "4px", fontFamily: "Roboto, sans-serif" }}>
                Applies to every schedule we build and every swap we suggest. You can still override
                it for a single week in the schedule builder.
              </Box>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <DietSelector
                value={diet}
                options={dietOptions}
                onChange={async (next) => {
                  const { ok, goalsReplaced } = await saveDiet(next);
                  let content = "Couldn't save that preference.";
                  if (ok) {
                    // Say it when the setting cost them a goal. The list is
                    // never shorter — the track hands over the next eligible
                    // goal — but a goal vanishing unannounced reads as a bug.
                    content =
                      goalsReplaced > 0
                        ? `Diet preference saved. ${goalsReplaced} goal${
                            goalsReplaced === 1 ? "" : "s"
                          } replaced with ${goalsReplaced === 1 ? "one" : "ones"} you can finish.`
                        : "Diet preference saved.";
                  }
                  setSnack({ open: true, content });
                }}
              />
            </Box>
          </Box>

          {/* Allergies / foods to avoid card */}
          <Box sx={{ ...cardSx, overflow: "visible" }}>
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: BORDER }}>
              <Box sx={cardLabelSx}>Allergies &amp; Foods to Avoid</Box>
              <Box sx={{ fontSize: 12, color: MUTED, mt: "4px", fontFamily: "Roboto, sans-serif" }}>
                We&apos;ll do our best to keep these out of your schedules — but recipes are
                AI-generated so always double-check
              </Box>
            </Box>

            <AllergySearch
              options={avoidOptions}
              blacklisted={blacklisted}
              onAdd={addBlacklisted}
            />

            {blacklisted.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  fontSize: 13,
                  color: MUTED,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                No restrictions set — search above to add
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", px: 3, py: 2 }}>
                {blacklisted.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      pl: "12px",
                      pr: "8px",
                      py: "5px",
                      bgcolor: RED_LIGHT,
                      borderRadius: "20px",
                      border: "1.5px solid",
                      borderColor: "#f5c0c5",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: RED,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {item.name}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => removeBlacklisted(item)}
                      sx={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: RED,
                        fontSize: 13,
                        lineHeight: 1,
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { opacity: 0.7 },
                      }}
                    >
                      ✕
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Learned taste. Sits directly below the avoid list because the two
              are easy to confuse and the contrast is the point: above is a hard
              exclusion the user declared, this is a soft nudge the app inferred
              and can be argued with. */}
          <TasteProfile
            profile={tasteProfile}
            onOverride={setTasteOverride}
            onClear={clearTasteOverride}
          />

          {/* Actions */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              border: "1.5px solid",
              borderColor: BORDER,
              px: 3,
              py: 2.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              component="button"
              onClick={handleLogout}
              sx={{
                padding: "8px 20px",
                bgcolor: "#f5f5f5",
                color: "#444",
                border: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": { bgcolor: "#eee" },
              }}
            >
              Log out
            </Box>
            <Box
              component="button"
              onClick={handleDelete}
              sx={{
                padding: "8px 20px",
                bgcolor: "transparent",
                color: "#e57373",
                border: "1.5px solid #e57373",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "#e57373", color: "#fff" },
              }}
            >
              Delete account
            </Box>
          </Box>
        </Box>
      </Box>
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        dateTime=""
        content={snack.content}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </DashboardLayout>
  );
}

export default Profile;
